import React, { useCallback, memo } from 'react';
import { Person } from '#root/queries/persons';
import { Badge } from '@fluentui/react-components';
import './styles.scss';
import { PERSON_ENUMS } from '#root/utils/persons';
import { getEnumLabelByValue, getUnitEnumLabelByValue } from '#root/utils/enums';
import LazyImage from '#root/components/LazyImage';

export interface MatchCardProps {
  victim: Person;
  publicPerson: Person;
  isSelected: boolean;
  onSelect: (publicPerson: Person) => void;
  unit: string;
  showMatchedBadge?: boolean;
}

const renderPhoto = (src?: string) => (
  <div className="person-image">
    <LazyImage
      src={src}
      fallback={<div className="person-image--no-image" />}
    />
  </div>
);

const MatchCard: React.FC<MatchCardProps> = memo(({
  victim,
  publicPerson,
  isSelected,
  onSelect,
  unit,
  showMatchedBadge
}) => {
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onSelect(publicPerson);
    }
  }, [publicPerson, onSelect]);

  const handleClick = useCallback(() => {
    onSelect(publicPerson);
  }, [publicPerson, onSelect]);

  const { firstName, lastName, gender, age, height, weight, hair, eyes, features,
    incidentName, unitId, department, status, restrictions, locationAddress, locationName } = victim;
  const publicContact = publicPerson.publicContact;
  const contactName = publicContact ? `${publicContact.firstName || ''} ${publicContact.lastName || ''}`.trim() : '';

  const isContacted = publicPerson.isConnected === true;
  const victimName = `${firstName || ''} ${lastName || ''}`.trim();
  const publicName = `${publicPerson.firstName || ''} ${publicPerson.lastName || ''}`.trim();

  return (
    <div
      className={`match-card large-card ${isSelected ? 'large-card--selected' : ''} ${isContacted ? 'match-card--contacted' : ''}`}
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="match-content">
        <div className="photos-section">
          {renderPhoto(publicPerson.pictureUrl)}
          {renderPhoto(victim.pictureUrl)}
        </div>
        <div className="info-container">
          <div className="info-container__left">
            <div className="large-card__title">
              {publicName}
              {victimName && <span className="match-card__secondary-name"> ({victimName})</span>}
            </div>
            <div className="info-columns">
              <div className="info-column">
                <div><span className='large-card__label'>Gender: </span>{getEnumLabelByValue(PERSON_ENUMS.GENDER, gender) ?? '-'}</div>
                <div><span className='large-card__label'>Age: </span>{getUnitEnumLabelByValue(PERSON_ENUMS.AGE_RANGES, age, unit) ?? '-'}</div>
                <div><span className='large-card__label'>Height: </span>{getUnitEnumLabelByValue(PERSON_ENUMS.HEIGHT_RANGES, height, unit) ?? '-'}</div>
                <div><span className='large-card__label'>Weight: </span>{getUnitEnumLabelByValue(PERSON_ENUMS.WEIGHT_RANGES, weight, unit) ?? '-'}</div>
              </div>
              <div className="info-column">
                <div><span className='large-card__label'>Hair: </span>{getEnumLabelByValue(PERSON_ENUMS.HAIR_COLORS, hair) ?? '-'}</div>
                <div><span className='large-card__label'>Eyes: </span>{getEnumLabelByValue(PERSON_ENUMS.EYE_COLORS, eyes) ?? '-'}</div>
                <div><span className='large-card__label'>Features: </span>{features?.map(feature => getEnumLabelByValue(PERSON_ENUMS.FEATURES, feature)).join(', ') || 'None'}</div>
                <div><span className='large-card__label'>Incident: </span>{incidentName}</div>
              </div>
            </div>
          </div>

          <div className="vertical-divider" />

          <div className="info-container__right">
            {(showMatchedBadge || isContacted) && (
              <div className="match-card__badge-row">
                {showMatchedBadge && (
                  <Badge appearance="filled" color="brand" size="medium">
                    Matched
                  </Badge>
                )}
                {isContacted && (
                  <Badge appearance="filled" color="success" size="medium">
                    Contacted
                  </Badge>
                )}
              </div>
            )}
            <div className="info-columns">
              <div className="info-column">
                <div><span className='large-card__label'>Contact: </span>{contactName ?? '-'}</div>
                <div><span className='large-card__label'>Email: </span>{publicContact?.email ?? '-'}</div>
                <div><span className='large-card__label'>Phone: </span>{publicContact?.phoneNumber ?? '-'}</div>
                <div><span className='large-card__label'>Relationship: </span>{getEnumLabelByValue(PERSON_ENUMS.RELATIONSHIPS, publicContact?.relationshipToPerson) ?? '-'}</div>
                <div><span className='large-card__label'>Unit ID: </span>{unitId ?? '-'}</div>
              </div>
              <div className="info-column">
                <div><span className='large-card__label'>Department: </span>{department ?? '-'}</div>
                <div><span className='large-card__label'>Location: </span>{locationName ?? '-'}</div>
                <div><span className='large-card__label'>Location Address: </span>{locationAddress ?? '-'}</div>
                <div><span className='large-card__label'>Status: </span>{status ?? '-'}</div>
                <div><span className='large-card__label'>Share Victim Info: </span>{restrictions ? 'No' : 'Yes'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

MatchCard.displayName = 'MatchCard';

export default MatchCard;
